import {Component, inject, OnInit} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {finalize} from 'rxjs';
import {NzAlertModule} from 'ng-zorro-antd/alert';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzCardModule} from 'ng-zorro-antd/card';
import {NzFormModule} from 'ng-zorro-antd/form';
import {NzInputNumberModule} from 'ng-zorro-antd/input-number';
import {NzMessageService} from 'ng-zorro-antd/message';
import {NzSpinModule} from 'ng-zorro-antd/spin';
import {NzSwitchModule} from 'ng-zorro-antd/switch';
import {AuthFlowService} from '../../core/services/auth-flow.service';
import {PsdRulesService} from '../../core/services/psd-rules.service';
import {SecurityPolicy} from '../../core/models/security-policy';

@Component({
  selector: 'app-admin-security-policy',
  standalone: true,
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    RouterLink,
    NzAlertModule,
    NzButtonModule,
    NzCardModule,
    NzFormModule,
    NzInputNumberModule,
    NzSpinModule,
    NzSwitchModule,
  ],
  templateUrl: './admin-security-policy.html',
  styleUrl: './admin-security-policy.css',
})
export class AdminSecurityPolicy implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly authFlow = inject(AuthFlowService);
  private readonly psdRulesService = inject(PsdRulesService);
  private readonly message = inject(NzMessageService);

  readonly form = new FormGroup({
    minLength: numberControl(6, 128),
    requireDigit: new FormControl(true, {nonNullable: true}),
    requireLowercase: new FormControl(true, {nonNullable: true}),
    requireUppercase: new FormControl(true, {nonNullable: true}),
    requireNonAlphanumeric: new FormControl(true, {nonNullable: true}),
    requiredUniqueChars: numberControl(0, 16),
    historyDepth: numberControl(0, 24),
    minAgeHours: numberControl(0, 720),
    maxAgeDays: numberControl(0, 3650),
    lockoutMaxFailedAttempts: numberControl(1, 50),
    lockoutDurationMinutes: numberControl(1, 10080),
    inactivityLockDays: numberControl(0, 3650),
  });

  isLoading = false;
  isSaving = false;
  errorMessage = '';
  updatedAt: string | null = null;

  ngOnInit(): void {
    this.load();
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    this.authFlow.updateSecurityPolicy(this.form.getRawValue() as SecurityPolicy)
      .pipe(finalize(() => this.isSaving = false))
      .subscribe({
        next: (policy) => {
          this.apply(policy);
          this.psdRulesService.reload();
          this.message.success(this.translate.instant('adminPolicy.saved'));
        },
        error: () => {
          this.errorMessage = this.translate.instant('adminPolicy.saveError');
        },
      });
  }

  reset(): void {
    this.load();
  }

  private load(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.authFlow.securityPolicy()
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (policy) => this.apply(policy),
        error: () => {
          this.errorMessage = this.translate.instant('adminPolicy.loadError');
        },
      });
  }

  private apply(policy: SecurityPolicy): void {
    this.updatedAt = policy.updatedAt ?? null;
    this.form.patchValue({
      minLength: policy.minLength,
      requireDigit: policy.requireDigit,
      requireLowercase: policy.requireLowercase,
      requireUppercase: policy.requireUppercase,
      requireNonAlphanumeric: policy.requireNonAlphanumeric,
      requiredUniqueChars: policy.requiredUniqueChars,
      historyDepth: policy.historyDepth,
      minAgeHours: policy.minAgeHours,
      maxAgeDays: policy.maxAgeDays,
      lockoutMaxFailedAttempts: policy.lockoutMaxFailedAttempts,
      lockoutDurationMinutes: policy.lockoutDurationMinutes,
      inactivityLockDays: policy.inactivityLockDays,
    });
    this.form.markAsPristine();
  }
}

function numberControl(min: number, max: number): FormControl<number> {
  return new FormControl(min, {
    nonNullable: true,
    validators: [Validators.required, Validators.min(min), Validators.max(max)],
  });
}
